// Shared constants and helpers for time-range annotation rendering. The two live
// render paths — the DOM overlay (AnnotationOverlay) and the standalone strip
// above the trend plot (RangeLaneBand) — import from here so they cannot drift
// in which annotations qualify as ranges, how lanes are bucketed/badged, or how
// times are formatted.

import { getAnnotationColor } from './annotationPresentation';
import { mergeOverlappingRanges } from './clusterUtils';

// Moment format for range timestamps, used in the modal and the in-chart
// hover/drag labels.
export const RANGE_TIME_FORMAT = 'MMM D, YYYY HH:mm';

// Only these annotation types may render as a horizontal time-range band. Every
// other type (task, repair, diagnostic_rule, machine_condition_change, …) is a
// point-in-time marker even when it carries an end_time. The array order also
// drives the stacking order of the per-type range lanes.
export const RANGE_CAPABLE_TYPES = ['alarm', 'note'];

// True when an annotation should render as a horizontal time-range band rather
// than a point marker: it is a range-capable type and carries a distinct
// end_time. Everything else renders through the point-in-time path.
export function isRangeAnnotation(annotation) {
  return !!(
    annotation
    && annotation.end_time
    && annotation.end_time !== annotation.start_time
    && RANGE_CAPABLE_TYPES.indexOf(annotation.type) !== -1
  );
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, value));
}

// Bucket range annotations into one lane per type and compute the shared
// per-lane geometry both render paths need: the colour, the per-range pills, the
// ×N overlap badges, the lane's track extent, and the count. Layout that differs
// between consumers (vertical `top`, track styling) is left to the caller.
//
// - `annotations`: the full annotation list (non-range entries are ignored).
// - `xPercentForTime(time) => number|null`: maps a timestamp to a percent within
//   the chart, or null when it can't be placed. Each consumer supplies its own
//   (the overlay maps against its static/zoom range, the band against the live
//   domain) so lanes always align with the data below. It must NOT clamp: pass
//   the raw percent, which may fall outside 0–100 for a range that extends past
//   the visible window. Clamping happens here, where the overflow can still be
//   recorded on the pill; a caller that pre-clamps just yields
//   continuesBefore/continuesAfter of false.
// - `options.gap`: epsilon (in percent) for fusing sub-unit slivers into a
//   single ×N badge without bridging real gaps.
//
// Returns `[{ type, color, count, items, pills, badges, trackLeft, trackWidth }]`
// ordered by RANGE_CAPABLE_TYPES.
export function buildRangeLanes(annotations, xPercentForTime, options = {}) {
  const { gap = 0 } = options;

  const byType = (annotations || []).reduce((acc, a) => {
    if (!isRangeAnnotation(a)) return acc;
    const s = xPercentForTime(a.start_time);
    const e = xPercentForTime(a.end_time);
    if (s === null || e === null) return acc;
    const rawStart = Math.min(s, e);
    const rawEnd = Math.max(s, e);
    // Guard the annotation's own geometry before trusting it: a mapper that
    // returns NaN (rather than null) for a corrupt timestamp would otherwise
    // pass both comparisons below and emit `left: NaN%` into the DOM.
    if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) return acc;
    // A range that does not intersect the visible window: clamping would
    // collapse it onto whichever edge it fell past and draw a phantom
    // zero-width pill (plus its boundary ticks) at a time the range never
    // covered. Drop it instead -- it has nothing to show in this window.
    // Inclusive bounds, so a range merely *touching* an edge is dropped too;
    // it would otherwise render as exactly that zero-width phantom.
    if (rawEnd <= 0 || rawStart >= 100) return acc;
    (acc[a.type] = acc[a.type] || []).push({
      ref: a,
      start: clampPercent(rawStart),
      end: clampPercent(rawEnd),
      // Clamping is lossy: once a pill is pinned to 0% / 100% it looks like a
      // range that starts or ends exactly at the window edge. Record which
      // sides were truncated so the render paths can show a "continues beyond"
      // affordance there instead of a boundary tick that claims otherwise.
      continuesBefore: rawStart < 0,
      continuesAfter: rawEnd > 100,
      type: a.type
    });
    return acc;
  }, {});

  const types = Object.keys(byType).sort((x, y) => {
    const ix = RANGE_CAPABLE_TYPES.indexOf(x);
    const iy = RANGE_CAPABLE_TYPES.indexOf(y);
    return (ix === -1 ? 99 : ix) - (iy === -1 ? 99 : iy);
  });

  return types.map((type) => {
    const items = byType[type];
    const color = getAnnotationColor(items[0].ref, { respectSeverity: false });
    const starts = items.map(i => i.start);
    const ends = items.map(i => i.end);
    const trackLeft = Math.min(...starts);
    const badges = mergeOverlappingRanges(items, { gap })
      .filter(c => c.items.length > 1)
      .map((c, i) => ({
        // Include the post-filter index and end% so two same-type clusters with
        // equal rounded start% and item count can't collide on a React key.
        key: `rb-${type}-${i}-${c.start.toFixed(2)}-${c.end.toFixed(2)}`,
        center: (c.start + c.end) / 2,
        items: c.items,
        count: c.items.length
      }));
    return {
      type,
      color,
      count: items.length,
      items,
      trackLeft,
      trackWidth: Math.max(0, Math.max(...ends) - trackLeft),
      pills: items.map(i => ({
        ref: i.ref,
        left: i.start,
        width: Math.max(0, i.end - i.start),
        continuesBefore: i.continuesBefore,
        continuesAfter: i.continuesAfter
      })),
      badges
    };
  });
}
